import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AvailableFileListColumns, File } from '../../../models/file';
import { FileControllerService } from '../../../services/file-controller.service';
import { FileListResult, GetUserFilesOpts } from '../../../interfaces/file';
import { environment } from '../../../../environments/environment';
import { BusinessEntity } from '../../../models/business-entity';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { DocumentPreviewComponent } from '../../document-preview/document-preview.component';
import { PaginationService } from 'ngx-pagination';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  Subscription,
  tap
} from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { plainToInstance } from 'class-transformer';
import { EregistrationsTranslationService } from '../../../services/eregistrations-translation.service';
import { GlobalStateProviderService } from '../../../services/global-state-provider.service';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { WebsocketService } from '../../../services/websocket.service';
import { WebSocketMessageType } from '../../../enums';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { ColombiaIframeService } from '../../../services/colombia-iframe.service';
import { BPASettings } from '@models/bpa-settings';
import { BpaSettingsService } from '../../../services/bpa-settings.service';

@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html'
})
export class FileListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() businessEntity: BusinessEntity | null = null;
  @Input() paginationId: string = 'applications';
  @Input() columns: AvailableFileListColumns[] = [
    AvailableFileListColumns.SERVICE_TITLE,
    AvailableFileListColumns.ENTERPRISE_NAME,
    AvailableFileListColumns.CREATED_AT,
    AvailableFileListColumns.SUBMITTED_AT,
    AvailableFileListColumns.CREATED_AT,
    AvailableFileListColumns.STATUS,
    AvailableFileListColumns.CERTIFICATE,
    AvailableFileListColumns.ACTIONS
  ];
  AvailableFileListColumns = AvailableFileListColumns;
  appsListSorting = { value: 'created_at', reverse: true };
  areApplicationsLoaded: boolean = false;
  hideListOfServices: boolean = false;
  bsModalRef?: BsModalRef;
  currentApplicationsOrdering: string = 'created_at';
  currentApplicationsFilter: string | undefined = '';
  currentApplicationsPage: number = 1;
  DATE_FORMAT: string = environment.DATE_FORMAT;
  fileList: File[] = [];
  limitedFileList: File[] = [];
  isApplicationsHeadingFiltersVisible: boolean = false;
  limitedView: boolean = true;
  responsiveAppsListSortVisible: boolean = false;
  responsiveAppsListSearchVisible: boolean = false;
  searchFilter = {
    service: '',
    servicePrev: '',
    company: '',
    companyPrev: '',
    unified: '',
    unifiedPrev: ''
  };
  searchSubscription?: Subscription;
  showLegend: boolean = false;
  totalFilesCount: number = 0;
  @Output() totalFilesCountEmitter = new EventEmitter<number>();
  @ViewChild('applicationsList') applicationsList!: ElementRef<HTMLElement>;
  private readonly popoverMap = new Map<any, PopoverDirective>();

  searchService$ = new BehaviorSubject('');
  searchCompany$ = new BehaviorSubject('');
  searchUnified$ = new BehaviorSubject('');
  noResults$ = new BehaviorSubject(false);
  noFilteredResults$ = new BehaviorSubject(false);

  faSearch = faSearch;

  private subscription?: Subscription;
  private bpaSettingsSubscription?: Subscription;
  private routeParamsSubscription?: Subscription;

  constructor(
    public fileController: FileControllerService,
    private readonly modalService: BsModalService,
    private readonly renderer: Renderer2,
    public paginationService: PaginationService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly eregistrationsTranslationService: EregistrationsTranslationService,
    public globalStateProviderService: GlobalStateProviderService,
    private readonly bpaSettings: BpaSettingsService,
    private readonly webSocket: WebsocketService,
    private readonly iframeService: ColombiaIframeService,
    private readonly http: HttpClient
  ) {}

  ngOnInit(): void {
    // Subscribe to BPA settings
    this.bpaSettingsSubscription = this.bpaSettings
      .getBpaSettings$()
      .subscribe((bpaSettings: BPASettings | null) => {
        if (bpaSettings) {
          this.hideListOfServices =
            !!bpaSettings.displaySystemProperties?.hide_list_of_services;
        }
      });
    // Subscribe to URL parameter changes
    this.routeParamsSubscription = this.route.queryParams.subscribe(
      (params) => {
        if (
          params['state'] !== undefined &&
          params['state'] !== this.currentApplicationsFilter
        ) {
          this.currentApplicationsFilter = params['state'];
          this.getUserFiles({});
        }
      }
    );

    this.searchSubscription = combineLatest([
      this.searchService$.pipe(debounceTime(500)),
      this.searchCompany$.pipe(debounceTime(500)),
      this.searchUnified$.pipe(debounceTime(500))
    ])
      .pipe(
        tap(([serviceSearch, companySearch, unifiedSearch]) => {
          this.searchFilter.service = serviceSearch;
          this.searchFilter.company = companySearch;
          this.searchFilter.unified = unifiedSearch;
          this.getUserFiles({});
        })
      )
      .subscribe();
    this.subscription = this.webSocket.messageReceived$.subscribe((message) => {
      if (message?.type === WebSocketMessageType.FILE_STATE_UPDATE) {
        this.getUserFiles({});
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.subscription?.unsubscribe();
    this.bpaSettingsSubscription?.unsubscribe();
    // Unsubscribe from route params subscription if it exists
    if (this.routeParamsSubscription) {
      this.routeParamsSubscription.unsubscribe();
    }
  }

  deleteFile(file: File) {
    this.fileController.delete(file.file_id).subscribe(() => {
      this.getUserFiles({});
    });
  }

  registerPopover(fileId: any, popover: PopoverDirective): void {
    this.popoverMap.set(fileId, popover);
  }

  hidePopover(fileId: string): void {
    const popover = this.popoverMap.get(fileId);
    if (popover) {
      popover.hide();
    } else {
      console.error('Could not find popover for fileId', fileId);
    }
  }

  docPreviewModal(file: File, certificateIndex: number = 0) {
    const items = file.certificates.map((cert, i) => {
      return {
        isCurrent: certificateIndex === i,
        documentName: cert.document_name,
        previewUrl: `${environment.BASE_API_URL}files/${file.file_id}/certificate-user/${cert.document_id}`
      };
    });

    // Check if running in iframe
    const isInIframe = this.iframeService.isRunningInIframe();

    if (isInIframe) {
      // If in iframe, directly get the current certificate and open in new tab
      const currentCert = items.find((item) => item.isCurrent);
      if (currentCert) {
        // Fetch the document and open in new tab
        this.http
          .get(currentCert.previewUrl, { responseType: 'blob' })
          .subscribe((result) => {
            const previewDoc = URL.createObjectURL(result);
            window.open(previewDoc, '_blank');
          });
      }
    } else {
      // If not in iframe, show the modal as usual
      const initialState: ModalOptions = {
        initialState: {
          serviceName: file.service_name,
          items
        },
        class: 'document-preview'
      };
      this.bsModalRef = this.modalService.show(
        DocumentPreviewComponent,
        initialState
      );
    }
  }

  getUserFiles(opts: GetUserFilesOpts) {
    this.initializeFileListState();
    this.applyOrderingToQuery(opts);
    this.applyStateFilterToQuery(opts);
    this.applySearchFiltersToQuery(opts);
    this.applyBusinessEntityToQuery(opts);
    opts.page = this.currentApplicationsPage;

    this.fileController.getUserFiles(opts).subscribe({
      next: (fileList) => this.handleFileListSuccess(fileList),
      error: () => this.handleFileListError()
    });
  }

  /**
   * Initializes the file list state and UI before fetching files
   */
  private initializeFileListState(): void {
    this.areApplicationsLoaded = false;
    this.limitedFileList.length = 0;
    this.fileList.length = 0;
    this.totalFilesCount = 0;
    this.noFilteredResults$.next(false);
    this.noResults$.next(false);

    if (!this.renderer || !this.applicationsList) {
      return;
    }

    this.renderer.addClass(this.applicationsList.nativeElement, 'placeholder');
    if (!this.limitedView) {
      this.renderer.addClass(
        this.applicationsList.nativeElement,
        'extended-list'
      );
    }
  }

  /**
   * Applies ordering parameters to the query options
   */
  private applyOrderingToQuery(opts: GetUserFilesOpts): void {
    if (!opts.order_by) {
      opts.order_by = this.currentApplicationsOrdering;
    }
    if (this.appsListSorting.reverse) {
      opts.order_by = `-${opts.order_by}`;
    }
  }

  /**
   * Applies state filter to the query options and updates URL
   */
  private applyStateFilterToQuery(opts: GetUserFilesOpts): void {
    if (opts.state) {
      if (this.currentApplicationsFilter !== opts.state) {
        this.currentApplicationsFilter = opts.state;
        this.currentApplicationsPage = 1;
      } else {
        this.currentApplicationsFilter = undefined;
        delete opts.state;
      }
    } else if (this.currentApplicationsFilter) {
      opts.state = this.currentApplicationsFilter;
    }

    this.updateUrlState();
  }

  /**
   * Applies search filters to the query options
   */
  private applySearchFiltersToQuery(opts: GetUserFilesOpts): void {
    if (this.searchFilter.unified) {
      this.applyUnifiedSearchFilter(opts);
    } else {
      this.applyIndividualSearchFilters(opts);
    }
  }

  /**
   * Applies unified search filter (searches across all fields)
   */
  private applyUnifiedSearchFilter(opts: GetUserFilesOpts): void {
    opts.unified = this.searchFilter.unified;
    opts.service = '';
    opts.company = '';

    if (this.searchFilter.unifiedPrev !== this.searchFilter.unified) {
      this.currentApplicationsPage = 1;
      this.searchFilter.unifiedPrev = this.searchFilter.unified;
    }
  }

  /**
   * Applies individual search filters (service and/or company)
   */
  private applyIndividualSearchFilters(opts: GetUserFilesOpts): void {
    if (this.searchFilter.service) {
      opts.service = this.searchFilter.service;
      opts.unified = '';
      if (this.searchFilter.servicePrev !== this.searchFilter.service) {
        this.currentApplicationsPage = 1;
        this.searchFilter.servicePrev = this.searchFilter.service;
      }
    }

    if (this.searchFilter.company) {
      opts.company = this.searchFilter.company;
      opts.unified = '';
      if (this.searchFilter.companyPrev !== this.searchFilter.company) {
        this.currentApplicationsPage = 1;
        this.searchFilter.companyPrev = this.searchFilter.company;
      }
    }
  }

  /**
   * Applies business entity filter to the query options
   */
  private applyBusinessEntityToQuery(opts: GetUserFilesOpts): void {
    if (this.businessEntity) {
      opts.businessEntity = this.businessEntity.business_entity_id;
    }
  }

  /**
   * Handles successful file list response
   */
  private handleFileListSuccess(fileList: FileListResult): void {
    if (this.renderer && this.applicationsList) {
      this.renderer.removeClass(
        this.applicationsList.nativeElement,
        'placeholder'
      );
    }

    this.processApplications(fileList);
    this.areApplicationsLoaded = true;
    this.noResults$.next(!this.fileList.length && !this.isFilterOn);
    this.noFilteredResults$.next(!this.fileList.length && this.isFilterOn);
  }

  /**
   * Handles file list fetch error
   */
  private handleFileListError(): void {
    this.areApplicationsLoaded = true;

    if (this.renderer && this.applicationsList) {
      this.renderer.removeClass(
        this.applicationsList.nativeElement,
        'placeholder'
      );
    }

    this.noResults$.next(!this.fileList.length && !this.isFilterOn);
    this.noFilteredResults$.next(!this.fileList.length && this.isFilterOn);
  }

  onApplicationsPageChange(page: number) {
    this.currentApplicationsPage = page;
    this.getUserFiles({});
  }

  processApplications(data: FileListResult) {
    if (data.count) {
      this.totalFilesCount = data.count;
    }

    this.fileList = plainToInstance(File, data.results);
    this.fileList.forEach((file: File) => {
      file.setListingState();
      this.eregistrationsTranslationService.idPushed(file.service_id);
    });

    this.limitedFileList = this.fileList.slice(0, 3);
    this.updateFileCount();
  }

  showFileProcessing(file: File) {
    let queryParams = {};
    const latestTask = file.getLatestTask();
    const isInApplicantRole =
      latestTask &&
      latestTask.status === 'filepending' &&
      latestTask.assignee === 'applicant';
    const mustGoToApplicant =
      isInApplicantRole ||
      file.fileState_code === 'to_correct' ||
      file.fileState_code === 'applicantPayment';
    if (file.process_instance_id && mustGoToApplicant) {
      queryParams = {
        process_instance_id: file.process_instance_id
      };
    } else {
      queryParams = {
        file_id: file.file_id
      };
    }
    this.router.navigate(['/services', file.service_id], {
      queryParams: queryParams
    });
  }

  sortFiles(orderBy: string) {
    this.currentApplicationsOrdering = orderBy;
    this.appsListSorting.value = orderBy;
    this.appsListSorting.reverse = !this.appsListSorting.reverse;
    this.getUserFiles({});
  }

  toggleLimitedView() {
    this.limitedView = !this.limitedView;
    this.limitedFileList = this.fileList.slice(0, 3);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isAnyChange = Object.keys(changes).some((key) => {
      // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
      return !changes[key].isFirstChange();
    });
    if (!isAnyChange) return;
    this.currentApplicationsPage = 1;
    this.getUserFiles({});
  }

  updateFileCount() {
    this.totalFilesCountEmitter.emit(this.totalFilesCount);
  }

  onFilterChange() {
    this.currentApplicationsPage = 1;
    this.getUserFiles({});
  }

  resetFilters() {
    this.searchFilter.service = '';
    this.searchFilter.company = '';
    this.searchFilter.unified = '';
    this.currentApplicationsFilter = undefined;
    this.updateUrlState(); // Update URL parameter immediately
    this.onFilterChange();
  }

  get isFilterOn() {
    if (
      this.searchFilter.service ||
      this.searchFilter.company ||
      this.searchFilter.unified
    ) {
      return true;
    }
    return Boolean(this.currentApplicationsFilter);
  }

  protected readonly environment = environment;

  /**
   * Updates the URL state parameter to match currentApplicationsFilter
   */
  private updateUrlState(): void {
    // Get current query params
    const queryParams = { ...this.route.snapshot.queryParams };

    // Update state parameter
    if (this.currentApplicationsFilter) {
      queryParams['state'] = this.currentApplicationsFilter;
    } else {
      delete queryParams['state'];
    }

    // Update URL without navigating away from the current view
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true
    });
  }
}
